import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMonitors, type MonitorFilters } from '../hooks/useMonitors';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { MonitorCard } from '../components/MonitorCard';
import { VoiceSearchModal } from '../components/VoiceSearchModal';
import { Button, Spinner } from '../components/ui';

type StatoAlertFilter = 'tutti' | 'good' | 'rare' | 'exceptional';
type StatoFilter = 'tutti' | 'true' | 'false';
type TipoFilter = 'tutti' | 'fisso' | 'flessibile';
type SortOption = 'recenti' | 'prezzo_basso' | 'stato_alert';


const ALERT_ORDER: Record<string, number> = {
  exceptional: 0,
  rare: 1,
  good: 2,
  none: 3,
};

function PillGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              value === opt.value
                ? 'bg-primary-500 text-white'
                : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyFilterState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="mb-4 w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-text-primary mb-1">
        {t('monitors.noResults')}
      </h3>
      <p className="text-sm text-text-secondary">
        {t('monitors.noResultsDescription')}
      </p>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="mb-6 w-24 h-24 rounded-full bg-primary-50 flex items-center justify-center">
        <svg
          className="w-12 h-12 text-primary-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">
        {t('monitors.emptyTitle')}
      </h2>
      <p className="text-text-secondary mb-6 max-w-md">
        {t('monitors.emptyDescription')}
      </p>
      <Link to="/onboarding">
        <Button size="lg">{t('monitors.emptyAction')}</Button>
      </Link>
    </div>
  );
}

export default function Monitors() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const showMic = user?.plan !== 'free';
  const [statoAlert, setStatoAlert] = useState<StatoAlertFilter>('tutti');
  const [stato, setStato] = useState<StatoFilter>('tutti');
  const [tipo, setTipo] = useState<TipoFilter>('tutti');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('recenti');
  const [currentPage, setCurrentPage] = useState(1);

  const STATO_ALERT_OPTIONS: { value: StatoAlertFilter; label: string }[] = [
    { value: 'tutti', label: t('monitors.allFilter') },
    { value: 'good', label: t('monitors.goodAlert') },
    { value: 'rare', label: t('monitors.rareAlert') },
    { value: 'exceptional', label: t('monitors.exceptionalAlert') },
  ];

  const STATO_OPTIONS: { value: StatoFilter; label: string }[] = [
    { value: 'tutti', label: t('monitors.allFilter') },
    { value: 'true', label: t('monitors.activeFilter') },
    { value: 'false', label: t('monitors.inactiveFilter') },
  ];

  const TIPO_OPTIONS: { value: TipoFilter; label: string }[] = [
    { value: 'tutti', label: t('monitors.allFilter') },
    { value: 'fisso', label: t('monitors.fixedDates') },
    { value: 'flessibile', label: t('monitors.flexiblePeriod') },
  ];

  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'recenti', label: t('monitors.sortRecent') },
    { value: 'prezzo_basso', label: t('monitors.sortPriceAsc') },
    { value: 'stato_alert', label: t('monitors.sortAlertStatus') },
  ];

  const PAGE_SIZE = 20;

  const apiFilters: MonitorFilters = useMemo(() => ({
    statoAlert: statoAlert !== 'tutti' ? statoAlert : undefined,
    attivo: stato !== 'tutti' ? stato : undefined,
    tipo: tipo !== 'tutti' ? tipo : undefined,
    page: currentPage,
    pageSize: PAGE_SIZE,
  }), [statoAlert, stato, tipo, currentPage]);

  const {
    monitors,
    pagination,
    isLoading,
    error,
    refetch,
    toggleActive,
    deleteMonitor,
  } = useMonitors(apiFilters);

  // Client-side search filter by airport codes
  const filteredMonitors = useMemo(() => {
    let result = monitors;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      result = result.filter(
        (m: any) =>
          m.aeroportoPartenza?.toUpperCase().includes(q) ||
          m.aeroportoArrivo?.toUpperCase().includes(q),
      );
    }
    return result;
  }, [monitors, search]);

  // Client-side sorting
  const sortedMonitors = useMemo(() => {
    const list = [...filteredMonitors];
    switch (sort) {
      case 'prezzo_basso':
        return list.sort((a: any, b: any) => {
          const pa = a.ultimoPrezzo != null ? Number(a.ultimoPrezzo) : Infinity;
          const pb = b.ultimoPrezzo != null ? Number(b.ultimoPrezzo) : Infinity;
          return pa - pb;
        });
      case 'stato_alert':
        return list.sort((a: any, b: any) => {
          const oa = ALERT_ORDER[a.statoAlert ?? 'none'] ?? 3;
          const ob = ALERT_ORDER[b.statoAlert ?? 'none'] ?? 3;
          return oa - ob;
        });
      case 'recenti':
      default:
        return list; // API already sorts by createdAt desc
    }
  }, [filteredMonitors, sort]);

  const hasAnyFilter = statoAlert !== 'tutti' || stato !== 'tutti' || tipo !== 'tutti' || search.trim() !== '';
  const totalCount = pagination?.total ?? monitors.length;

  // Reset page when filters change
  const handleFilterChange = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setCurrentPage(1);
  };

  if (isLoading && monitors.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && monitors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={() => refetch()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  // Show empty state only when there are no monitors at all (no filters applied)
  if (totalCount === 0 && !hasAnyFilter && !isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
          {t('monitors.title')}
        </h1>
        <div className="flex items-center gap-2">
          {showMic && (
            <button
              onClick={() => setVoiceOpen(true)}
              className="h-10 w-10 rounded-lg border border-border bg-surface text-text-secondary flex items-center justify-center hover:bg-surface-secondary active:scale-95 transition-all cursor-pointer"
              aria-label={t('monitors.voiceSearchAria')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
            </button>
          )}
          <Link to="/onboarding">
            <Button
              iconLeft={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              {t('monitors.newMonitor')}
            </Button>
          </Link>
        </div>
      </div>

      <p className="text-sm text-text-secondary mb-6">
        {totalCount} {totalCount === 1 ? t('monitors.totalCountSingular') : t('monitors.totalCount')}
      </p>

      {/* Filters */}
      <div className="space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <PillGroup
            label={t('monitors.alertStatus')}
            options={STATO_ALERT_OPTIONS}
            value={statoAlert}
            onChange={handleFilterChange(setStatoAlert)}
          />
          <PillGroup
            label={t('monitors.status')}
            options={STATO_OPTIONS}
            value={stato}
            onChange={handleFilterChange(setStato)}
          />
          <PillGroup
            label={t('monitors.type')}
            options={TIPO_OPTIONS}
            value={tipo}
            onChange={handleFilterChange(setTipo)}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          {/* Search */}
          <div className="flex-1">
            <label htmlFor="monitor-search" className="text-xs font-medium text-text-secondary mb-1.5 block">
              {t('monitors.searchRoute')}
            </label>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                id="monitor-search"
                type="text"
                placeholder={t('monitors.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-surface text-text-primary placeholder:text-text-secondary text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="sm:w-48">
            <label htmlFor="monitor-sort" className="text-xs font-medium text-text-secondary mb-1.5 block">
              {t('monitors.sortBy')}
            </label>
            <select
              id="monitor-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOption)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Monitor list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : sortedMonitors.length === 0 ? (
        <EmptyFilterState />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedMonitors.map((monitor) => (
              <MonitorCard
                key={monitor.id}
                monitor={monitor}
                onToggleActive={toggleActive}
                onDelete={deleteMonitor}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasPrevious}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                {t('monitors.previous')}
              </Button>
              <span className="text-sm text-text-secondary">
                {t('monitors.pageOf').replace('{page}', String(pagination.page)).replace('{total}', String(pagination.totalPages))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasNext}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                {t('monitors.next')}
              </Button>
            </div>
          )}
        </>
      )}

      {showMic && <VoiceSearchModal open={voiceOpen} onClose={() => setVoiceOpen(false)} />}
    </div>
  );
}
